/**
 * WebAuthn passkey helpers for browser/PWA (fingerprint, Face ID, etc.).
 * Uses app API routes + @simplewebauthn/browser.
 */

import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from '@simplewebauthn/browser';

const getApiBase = () => {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
};

const FETCH_TIMEOUT_MS = 25_000;

function fetchWithTimeout(url: string, init: RequestInit & { timeout?: number } = {}): Promise<Response> {
  const { timeout = FETCH_TIMEOUT_MS, ...rest } = init;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  return fetch(url, { ...rest, signal: controller.signal }).finally(() => clearTimeout(id));
}

export function isPasskeySupported(): boolean {
  return browserSupportsWebAuthn();
}

export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

/**
 * Fetch registration options (requires authenticated user), run credential create, send to API.
 */
export async function registerPasskey(authHeader: string): Promise<{ verified: boolean; error?: string }> {
  const trimmed = typeof authHeader === 'string' ? authHeader.trim() : '';
  if (!trimmed || !/^Bearer\s+.+$/i.test(trimmed) || trimmed.toLowerCase().startsWith('bearer undefined')) {
    return { verified: false, error: 'Session expired. Please sign out and sign in again.' };
  }
  const base = getApiBase();
  const optsRes = await fetch(`${base}/api/passkey?step=register-options`, {
    method: 'GET',
    headers: { Authorization: trimmed },
  });
  if (!optsRes.ok) {
    const err = (await optsRes.json().catch(() => ({}))) as { error?: string; detail?: string };
    const msg = err?.error || 'Failed to get registration options';
    const detail = err?.detail ? ` ${err.detail}` : '';
    return { verified: false, error: msg + detail };
  }
  const optionsJSON = await optsRes.json();

  let attResp: unknown;
  try {
    attResp = await startRegistration({ optionsJSON });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    const lower = msg.toLowerCase();
    if (
      lower.includes('previously registered') ||
      lower.includes('already registered') ||
      lower.includes('excludecredentials')
    ) {
      return {
        verified: true,
        error: 'Passkey already saved on this device. You can sign in with it now.',
      };
    }
    // Show the raw browser error so users/devs can diagnose
    return { verified: false, error: msg || 'Passkey registration was cancelled or failed.' };
  }

  const verifyRes = await fetch(`${base}/api/passkey?step=register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(attResp),
  });
  const verifyData = (await verifyRes.json().catch(() => ({}))) as {
    error?: string;
    detail?: string;
    verified?: boolean;
  };
  if (!verifyRes.ok) {
    const msg = verifyData?.error || 'Registration failed';
    const detail = verifyData?.detail ? ` ${verifyData.detail}` : '';
    return { verified: false, error: msg + detail };
  }
  return { verified: verifyData?.verified === true };
}

/** Fetch auth options and cache them for immediate use on click (avoids user-gesture timing issues). */
export async function fetchPasskeyAuthOptions(): Promise<
  | {
      ok: true;
      optionsJSON: Record<string, unknown>;
    }
  | { ok: false; error: string }
> {
  const base = getApiBase();
  try {
    const optsRes = await fetchWithTimeout(`${base}/api/passkey?step=auth-options`, { method: 'GET' });
    if (!optsRes.ok) {
      const err = await optsRes.json().catch(() => ({}));
      return {
        ok: false,
        error: (err as { error?: string }).error || 'Failed to get authentication options',
      };
    }
    const optionsJSON = await optsRes.json();
    return { ok: true, optionsJSON };
  } catch (e: unknown) {
    return { ok: false, error: e instanceof Error ? e.message : 'Failed to fetch passkey options' };
  }
}

/**
 * Fetch auth options (or use pre-fetched ones), run credential get, send to API. Returns { token, email } for verifyOtp.
 * Pass `cachedOptionsJSON` if options were pre-fetched to avoid async gap between user click and WebAuthn prompt.
 */
export async function authenticatePasskey(
  cachedOptionsJSON?: Record<string, unknown>,
): Promise<{ ok: true; token: string; email: string } | { ok: false; error: string }> {
  const base = getApiBase();

  let optionsJSON: Record<string, unknown>;
  if (cachedOptionsJSON) {
    optionsJSON = cachedOptionsJSON;
  } else {
    const optsRes = await fetchWithTimeout(`${base}/api/passkey?step=auth-options`, { method: 'GET' });
    if (!optsRes.ok) {
      const err = await optsRes.json().catch(() => ({}));
      return {
        ok: false,
        error: (err as { error?: string }).error || 'Failed to get authentication options',
      };
    }
    optionsJSON = await optsRes.json();
  }

  let asseResp: unknown;
  try {
    // useBrowserAutofill: false so the OS credential picker shows when triggered by the button (avoids hang when autofill UI never appears)
    asseResp = await startAuthentication({
      optionsJSON: optionsJSON as any,
      useBrowserAutofill: false,
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    const lower = msg.toLowerCase();
    if (
      lower.includes('no credential') ||
      lower.includes('no passkey') ||
      lower.includes('passkey does not exist') ||
      lower.includes('notallowed') ||
      (e instanceof Error && e.name === 'NotAllowedError')
    ) {
      return {
        ok: false,
        error:
          'No passkey on this device. Sign in with password or OTP, then add a passkey from your account menu to use it next time.',
      };
    }
    return { ok: false, error: msg || 'Authentication was cancelled or failed' };
  }

  const verifyRes = await fetchWithTimeout(`${base}/api/passkey?step=auth`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(asseResp),
  });
  const data = (await verifyRes.json().catch(() => ({}))) as {
    error?: string;
    detail?: string;
    token?: string;
    email?: string;
    verified?: boolean;
  };
  if (!verifyRes.ok) {
    const msg = data?.error || 'Verification failed';
    const detail = data?.detail ? ` ${data.detail}` : '';
    return { ok: false, error: msg + detail };
  }
  if (data.verified && data.token && data.email) {
    return { ok: true, token: data.token, email: data.email };
  }
  // Show what we actually got back for debugging
  const missing = [!data.verified && 'verified', !data.token && 'token', !data.email && 'email']
    .filter(Boolean)
    .join(', ');
  return { ok: false, error: `Server response missing: ${missing}` };
}
